import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import ReactLoading from 'react-loading'
import {
  ExpressCheckoutElement,
  PaymentElement,
  Elements,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js'
import { useContext } from 'react'
import { ProductBusketContext } from '../../layouts/ProductLayout'
import { GlobalContext } from '../../appContext/GlobalContext'
import { getProcessingCost, getTotalCost, getTotalCostLocationBased } from '../../utils/price'
import { Button } from '../../common'
import { toast } from 'react-hot-toast'
import { formatPrice } from '../../utils/number'
import { useLocation, useNavigate } from 'react-router-dom'
import { loadStripe } from '@stripe/stripe-js'
import { IoIosArrowForward } from 'react-icons/io'
import RectangleButton from '../../common/RectangleButton'
import { RxCross2 } from 'react-icons/rx'
import { useTranslation } from 'react-i18next'
import convertTimeStringToDateISO from '../../utils/convertTimeStringToDateISO'

const configKey = process.env.REACT_APP_PUBLISHABLE_KEY
const shouldLogStripeDebug = process.env.NODE_ENV !== 'production'

const getPaymentIntentFailureMessage = (paymentIntentResponse) => {
  return (
    paymentIntentResponse?.stripeMessage ||
    paymentIntentResponse?.message ||
    'Unable to initialize card payment. Please try again.'
  )
}

const logPaymentIntentDebug = (label, payload) => {
  if (shouldLogStripeDebug) {
    console.log(label, payload)
  }
}

// ----------------------------------------------------------------------
// NEW: Helper to compute total charge including Stripe and adhoc fees
// All amounts are in base currency (RON, USD, etc.) – not cents
// ----------------------------------------------------------------------
const computeCharges = ({
  netAmount,
  stripeRate,
  stripeFixed,
  adhocFee,
}) => {
  stripeRate = parseFloat(stripeRate)
  stripeFixed = parseFloat(stripeFixed)
  adhocFee = parseFloat(adhocFee)

  // Total charge before any rounding
  const totalCharge = (netAmount + stripeFixed + adhocFee) / (1 - stripeRate)

  // Stripe fee = percentage of totalCharge + fixed fee
  const stripeFee = stripeRate * totalCharge + stripeFixed

  return {
    totalCharge,          // customer pays this (raw value)
    stripeFee,            // Stripe's fee (raw value)
    adhocFee,             // your platform fee
    netToMerchant: totalCharge - stripeFee - adhocFee,
  }
}

// ----------------------------------------------------------------------
// CheckoutForm now receives totalCharge and adhocFee as props
// ----------------------------------------------------------------------
const CheckoutForm = ({ stripeConnectedAccount, discountAmount, promoCodeDiscount, totalCharge, adhocFee, stripeFee, setIsProcessingOrder, setProcessingStatus, fetchPaymentIntent }) => {
  const { t } = useTranslation()

  const navigate = useNavigate()
  const stripe = useStripe()
  const elements = useElements()
  const { busket, getCompleteInvoice, tips, delivPickupType, deliveryData, setDeliveryData, pickupData, setPickupData, delivPickupPrice } = useContext(ProductBusketContext)
  const { colorCode, location, method, adhocCommissionEnabled,
    adhocCommissionValue,
    stripeCommissionEnabled,
    stripeFixedCommission,
    stripePercentageCommission } = useContext(GlobalContext)
  const [isLoading, setIsLoading] = useState(false)
  const [email, setEmail] = useState()
  const [customerName, setCustomerName] = useState('')

  let old_busket = localStorage.getItem('merged_busket');
  if (old_busket) {
    old_busket = JSON.parse(old_busket)
  }

  const productTotal = getTotalCostLocationBased(old_busket ? old_busket : busket, location.LocationId)

  const validEmailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/

  const { state } = useLocation();
  const { pay_later_payment } = state || {};

  const handleChangeEmail = (e) => {
    setEmail(e.target.value)
    setDeliveryData(prev => ({
      ...prev,
      email: e.target.value
    }))
  }

  const handleChangeName = (e) => {
    setCustomerName(e.target.value)
    setDeliveryData(prev => ({
      ...prev,
      firstName: e.target.value
    }))
    setPickupData(prev => ({
      ...prev,
      firstName: e.target.value
    }))
  }

  const handleSubmit = async (event) => {
    event.preventDefault()

    if (!email.match(validEmailRegex)) {
      toast.error('Email is not valid', {
        position: 'bottom-right',
      })
      setIsLoading(false)
      return
    }

    if (!stripe || !elements) {
      return
    }

    setIsLoading(true)
    setIsProcessingOrder?.(true)
    setProcessingStatus?.('authorizing')

    // Trigger form validation and wallet collection
    const { error: submitError } = await elements.submit()
    if (submitError) {
      toast.error(submitError.message, {
        position: 'bottom-right',
      })
      setIsLoading(false)
      setIsProcessingOrder?.(false)
      setProcessingStatus?.('')
      return
    }

    toast.loading('We are processing, please wait a bit..', {
      position: 'bottom-right',
      duration: 2000,
    })

    // Use the pre‑computed totalCharge (customer pays this) and adhocFee
    const amountInCents = Math.round(totalCharge * 100)
    const platformFeeInCents = Math.round(adhocFee * 100)
    const stripeFeeInCents = Math.round(stripeFee * 100)

    let clientSecret
    let paymentIntentId

    try {
      const paymentIntentResponse = fetchPaymentIntent
        ? await fetchPaymentIntent()
        : await (async () => {
            const controller = new AbortController()
            const timeoutId = setTimeout(() => controller.abort(), 12000)
            const res = await fetch(`${process.env.REACT_APP_API_URL}/order/create/create-payment-intent`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                amount: amountInCents,
                stripeConnectedAccount: stripeConnectedAccount,
                platformFee: platformFeeInCents,
                stripeFee: stripeFeeInCents,
              }),
              signal: controller.signal,
            })
            clearTimeout(timeoutId)
            return await res.json()
          })()

      clientSecret = paymentIntentResponse?.clientSecret
      paymentIntentId = paymentIntentResponse?.paymentIntentId

      if (!clientSecret) {
        logPaymentIntentDebug('Stripe PaymentIntent creation failed', {
          response: paymentIntentResponse,
          stripeScope: stripeConnectedAccount ? 'connected_account_available' : 'platform_only',
        })
        toast.error(getPaymentIntentFailureMessage(paymentIntentResponse), {
          position: 'bottom-right',
        })
        setIsLoading(false)
        setIsProcessingOrder?.(false)
        setProcessingStatus?.('')
        return
      }
    } catch (err) {
      logPaymentIntentDebug('Stripe PaymentIntent request crashed', {
        message: err.message,
      })
      const isTimeout = err.name === 'AbortError'
      toast.error(
        isTimeout
          ? 'Payment authorization timed out. Please try again.'
          : err.message || 'Unable to initialize card payment. Please try again.',
        {
          position: 'bottom-right',
        }
      )
      setIsLoading(false)
      setIsProcessingOrder?.(false)
      setProcessingStatus?.('')
      return
    }

    const { error } = await stripe.confirmPayment({
      elements,
      clientSecret,
      confirmParams: {
        return_url: `${window.location.origin}/food-ordering/thankYou?location=${location?.LocationId || ''}`,
        payment_method_data: {
          billing_details: {
            email: email,
            name: customerName,
          },
        },
      },
      redirect: 'if_required',
    })

    if (error) {
      toast.error(error.message, {
        position: 'bottom-right',
      })
      setIsLoading(false)
      setIsProcessingOrder?.(false)
      setProcessingStatus?.('')
      return
    } else {
      setProcessingStatus?.('creating_order')
      toast.loading('Almost there..', {
        position: 'bottom-right',
        duration: 2000,
      })

      if (pay_later_payment) {
        let currentPaylaterOrder = JSON.parse(localStorage.getItem('currentPaylaterOrder'))
        let tips = JSON.parse(localStorage.getItem('tips'))

        const dataBody = {
          orderId: currentPaylaterOrder['order_id'],
          ...getCompleteInvoice(),
          req_mode: 'update_and_pay',
          paymentIntentId,
          email,
          tips: formatPrice(tips),
          discounts_products: productTotal['discountedTotalPrice'] > 0 ? (productTotal['result'] - productTotal['discountedTotalPrice']) : 0,
          stripeConnectedAccount,
          discount_using_promo: discountAmount,
          promo_code_applied: promoCodeDiscount
        }
        dataBody['order']['items'] = []
        setIsLoading(true)
        fetch(`${process.env.REACT_APP_API_URL}/order/create/paylater_order`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(dataBody),
        })
          .then((res) => res.json())
          .then((data) => {
            toast.dismiss('order-processing-toast')
            if (data['orderId']) {
              navigate('/food-ordering/thankYou', {
                state: {
                  location: `${location?.LocationId}`,
                  pay_method: 'pay_later',
                  pay_later_status: 'payment_completed'
                },
              })
            } else {
              throw Error(`Some error occured`)
            }
          })
          .catch((err) => {
            toast.dismiss('order-processing-toast')
            setIsProcessingOrder?.(false)
            toast.error(err.message, {
              position: 'bottom-right',
            })
            navigate('/food-ordering/payment-failed',
              {
                state: {
                  location: `${location?.LocationId}`
                }
              })
          })
          .finally(() => {
            setIsLoading(false)
          })
      } else {
        const dataBody = delivPickupType == 'pickup' ? {
          ...getCompleteInvoice(),
          paymentIntentId,
          email,
          tips: formatPrice(tips),
          discounts_products: productTotal['discountedTotalPrice'] > 0 ? (productTotal['result'] - productTotal['discountedTotalPrice']) : 0,
          discount_using_promo: discountAmount,
          promo_code_applied: promoCodeDiscount,
          stripeConnectedAccount,
          type: "pickup",
          deliveryDate: convertTimeStringToDateISO(`${pickupData.day}, ${pickupData.time}`),
          delivery_phone: pickupData.phone,
          delivery_email: email,
          delivery_firstName: pickupData.firstName.split(' ')[0] ?? '',
          delivery_lastName: pickupData.firstName.split(' ')[1] ?? '',
        }
          : delivPickupType == 'delivery' ? {
            ...getCompleteInvoice(),
            paymentIntentId,
            email,
            tips: formatPrice(tips),
            discounts: (productTotal['result'] - productTotal['discountedTotalPrice']),
            stripeConnectedAccount,
            type: "delivery",
            deliveryAddress: {
              "postalCode": deliveryData.postalCode.toString(),
              "street": deliveryData.street,
              "city": deliveryData.city,
              "county": deliveryData.county,
              "long": deliveryData.long,
              "lat": deliveryData.lat,
              "country": deliveryData.country,
              "isDeliveryAddress": true,
              "isInvoiceAddress": true,
              "details": deliveryData.details
            },
            "noteForCourier": deliveryData.noteForCourier,
            "delivery_firstName": deliveryData.firstName,
            "delivery_lastName": deliveryData.lastName,
            "delivery_phone": deliveryData.phone.toString(),
            "delivery_email": deliveryData.email
          }
            : {
              ...getCompleteInvoice(),
              paymentIntentId,
              email,
              tips: formatPrice(tips),
              discounts_products: productTotal['discountedTotalPrice'] > 0 ? (productTotal['result'] - productTotal['discountedTotalPrice']) : 0,
              stripeConnectedAccount,
              discount_using_promo: discountAmount,
              promo_code_applied: promoCodeDiscount
            }
        fetch(`${process.env.REACT_APP_API_URL}/order/create`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(dataBody),
        })
          .then((res) => res.json())
          .then((data) => {
            if (data['success']) {
              if (delivPickupType == 'pickup' && (data?.orderNo || data?.paymentIntent?.newInvoiceNumber)) {
                if (data?.orderNo) window.localStorage.setItem("thankyou_code", data.orderNo)
                else if (data?.paymentIntent?.newInvoiceNumber) window.localStorage.setItem("thankyou_code", data?.paymentIntent?.newInvoiceNumber)
              }
              navigate(
                '/food-ordering/thankYou',
                {
                  state: {
                    location: `${location?.LocationId}`,
                    discountAmount: discountAmount,
                    promoCodeDiscount: promoCodeDiscount
                  }
                }
              )
            } else {
              throw Error(`Some error occured`)
            }
          }).catch(err => {
            toast.error(err.message, {
              position: 'bottom-right',
            })
            navigate('/food-ordering/payment-failed',
              {
                state: {
                  location: `${location?.LocationId}`
                }
              })
          }).finally(() => {
            setIsLoading(false)
          })
      }
    }
  }

  const paymentElementOptions = {
    layout: 'tabs',
    paymentMethodTypes: ['card']
  }

  return (
    <form id="payment-form" onSubmit={handleSubmit}>
      <div>
        <h4 className="font-normal mt-[5px] text-[16px]">
          {t('product.owners_email')}
        </h4>
        <input
          className="font-normal shadow border border-[#F9F9F9] rounded-[18px] text-[16px] bg-white text-black py-2 px-4 transition duration-200 ease-in-out outline-none w-full"
          style={{
            fontFamily: 'sans-serif !important',
          }}
          required
          type="email"
          name="cardHolderEmail"
          placeholder="john.doe@adhocorder.ro"
          onChange={(e) => handleChangeEmail(e)}
        />
      </div>
      <div>
        <h4 className="font-normal mt-[5px] text-[16px]">
          {t('product.holders_name')}
        </h4>
        <input
          className="font-normal shadow border border-[#F9F9F9] mb-[5px] text-[16px] rounded-[18px] bg-white text-black py-2 px-4 transition duration-200 ease-in-out outline-none w-full"
          style={{
            fontFamily: 'sans-serif !important',
          }}
          required
          name="cardHolderName"
          placeholder={t('product.cardHolder')}
          onChange={(e) => handleChangeName(e)}
        />
      </div>
      <PaymentElement id="payment-element" options={paymentElementOptions} />
      <Button
        id="submit"
        colorCode={colorCode}
        className="w-full mt-2 font-medium"
        type="submit"
        text={isLoading ? `${t('product.order_sent')} ...` : t('product.confirm_payment')}
        disabled={isLoading || !stripe || !elements}
      />
      <p className="mt-2 text-[14px] sm:text-center font-medium">
        {t('product.payment_notes')}
      </p>
      <p className="mt-2 text-[14px] sm:text-center font-medium">
        {t('product.need_assist')}{' '}
        <a
          className="cursor-pointer text-[14px]"
          style={{ color: colorCode }}
          href="https://adhocorder.ro/help-and-support"
        >
          {t('product.contact_us')}
        </a>
      </p>
    </form>
  )
}

const CheckoutFormPayPal = ({ amount, currency }) => {
  return (
    <div>
      <div>
        <div className="flex w-full justify-between">
          <p className="mt-7">Pay with</p>
          <div className="flex items-center mt-4 ml-6">
            <RectangleButton
              variant="icon"
              text={`Change`}
              icon={<IoIosArrowForward />}
              className="font-normal bg-[#FFF] border-0 hover:bg-[#FFF]"
            />
          </div>
        </div>
        <div className="mb-4 flex items-center justify-start">
          <img
            src="/assets/images/icons/visa.png"
            alt="VISA"
            className="mr-2"
          />
          <p className="m-0">Visa x-0542</p>
        </div>
      </div>
      <p className="font-semibold">
        {formatPrice(amount)} <span className="uppercase">{currency}</span>
      </p>
      <Button
        className="w-full mt-12"
        type="submit"
        text={`Confirm Payment Method`}
      />
    </div>
  )
}

// ----------------------------------------------------------------------
// ExpressCheckoutPage also receives totalCharge and adhocFee
// ----------------------------------------------------------------------
const ExpressCheckoutPage = ({ stripeConnectedAccount, discountAmount, promoCodeDiscount, totalCharge, adhocFee, stripeFee, setIsProcessingOrder, setProcessingStatus, fetchPaymentIntent }) => {
  const navigate = useNavigate()
  const stripe = useStripe()
  const elements = useElements()
  const { busket, getCompleteInvoice, tips, delivPickupType, deliveryData, pickupData, delivPickupPrice } = useContext(ProductBusketContext)
  const { location, method, adhocCommissionEnabled,
    adhocCommissionValue,
    stripeCommissionEnabled,
    stripeFixedCommission,
    stripePercentageCommission } = useContext(GlobalContext)

  let old_busket = localStorage.getItem('merged_busket');
  if (old_busket) {
    old_busket = JSON.parse(old_busket)
  }

  const productTotal = getTotalCostLocationBased(old_busket ? old_busket : busket, location.LocationId)

  const [isLoading, setIsLoading] = useState(false)

  const { state } = useLocation();
  const { pay_later_payment } = state || {};

  const onConfirm = async (e) => {
    if (!stripe || !elements) {
      try {
        e?.paymentFailed?.({ reason: 'fail' })
      } catch (err) {
        console.error('Error in paymentFailed callback:', err)
      }
      return
    }

    setIsLoading(true)

    // Helper to safely fail BEFORE stripe.confirmPayment is called.
    // In Google Pay on Android, calling e.paymentFailed() immediately resolves the
    // payment authorization callback with failure. This prevents Google Pay from
    // hanging on its authorization spinner for 30s and throwing callback_timed_out.
    const failBeforeConfirm = (message) => {
      try {
        e?.paymentFailed?.({ reason: 'fail' })
      } catch (err) {
        console.error('Error in paymentFailed callback:', err)
      }
      if (message) {
        toast.error(message, {
          position: 'bottom-right',
        })
      }
      setIsLoading(false)
      setIsProcessingOrder?.(false)
      setProcessingStatus?.('')
    }

    // 1. Submit Elements to validate and collect wallet details
    let submitResult
    try {
      submitResult = await elements.submit()
    } catch (err) {
      failBeforeConfirm(err.message || 'Validation failed')
      return
    }

    if (submitResult?.error) {
      failBeforeConfirm(submitResult.error.message)
      return
    }

    // 2. Fetch or retrieve pre-fetched PaymentIntent clientSecret
    const amountInCents = Math.round(totalCharge * 100)
    const platformFeeInCents = Math.round(adhocFee * 100)
    const stripeFeeInCents = Math.round(stripeFee * 100)

    let clientSecret
    let paymentIntentId

    try {
      const paymentIntentResponse = fetchPaymentIntent
        ? await fetchPaymentIntent()
        : await (async () => {
            const controller = new AbortController()
            const timeoutId = setTimeout(() => controller.abort(), 12000)
            const res = await fetch(`${process.env.REACT_APP_API_URL}/order/create/create-payment-intent`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                amount: amountInCents,
                stripeConnectedAccount: stripeConnectedAccount,
                platformFee: platformFeeInCents,
                stripeFee: stripeFeeInCents,
              }),
              signal: controller.signal,
            })
            clearTimeout(timeoutId)
            return await res.json()
          })()

      clientSecret = paymentIntentResponse?.clientSecret
      paymentIntentId = paymentIntentResponse?.paymentIntentId

      if (!clientSecret) {
        logPaymentIntentDebug('Stripe PaymentIntent creation failed', {
          response: paymentIntentResponse,
          stripeScope: stripeConnectedAccount ? 'connected_account_available' : 'platform_only',
        })
        failBeforeConfirm(getPaymentIntentFailureMessage(paymentIntentResponse))
        return
      }
    } catch (err) {
      logPaymentIntentDebug('Stripe PaymentIntent request crashed', {
        message: err.message,
      })
      const isTimeout = err.name === 'AbortError'
      failBeforeConfirm(
        isTimeout
          ? 'Payment authorization timed out. Please try again.'
          : err.message || 'Unable to initialize card payment. Please try again.'
      )
      return
    }

    // 3. Confirm Payment with Stripe
    let confirmResult
    try {
      confirmResult = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: `${window.location.origin}/food-ordering/thankYou?location=${location?.LocationId || ''}`,
        },
        redirect: 'if_required',
      })
    } catch (err) {
      // NOTE: stripe.confirmPayment already handled the callback outcome with Google Pay,
      // so we MUST NOT call e.paymentFailed() here as it would throw an IntegrationError.
      if (err.message) {
        toast.error(err.message, {
          position: 'bottom-right',
        })
      }
      setIsLoading(false)
      setIsProcessingOrder?.(false)
      setProcessingStatus?.('')
      return
    }

    if (confirmResult?.error) {
      // NOTE: stripe.confirmPayment already notified Google Pay of the outcome.
      if (confirmResult.error.message) {
        toast.error(confirmResult.error.message, {
          position: 'bottom-right',
        })
      }
      setIsLoading(false)
      setIsProcessingOrder?.(false)
      setProcessingStatus?.('')
      return
    } else {
      setIsProcessingOrder?.(true)
      setProcessingStatus?.('creating_order')
      toast.loading('We are processing, please wait a bit..', {
        id: 'order-processing-toast',
      })
      const email =
        window.localStorage.getItem('email') ?? e?.billingDetails?.email ?? ''

      if (pay_later_payment) {
        let currentPaylaterOrder = JSON.parse(localStorage.getItem('currentPaylaterOrder'))
        let tips = JSON.parse(localStorage.getItem('tips'))

        const dataBody = {
          orderId: currentPaylaterOrder['order_id'],
          ...getCompleteInvoice(),
          req_mode: 'update_and_pay',
          paymentIntentId,
          email,
          tips: formatPrice(tips),
          discounts_products: productTotal['discountedTotalPrice'] > 0 ? (productTotal['result'] - productTotal['discountedTotalPrice']) : 0,
          stripeConnectedAccount,
          discount_using_promo: discountAmount,
          promo_code_applied: promoCodeDiscount
        }
        dataBody['order']['items'] = []
        setIsLoading(true)
        fetch(`${process.env.REACT_APP_API_URL}/order/create/paylater_order`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(dataBody),
        })
          .then((res) => res.json())
          .then((data) => {
            toast.dismiss('order-processing-toast')
            if (data['orderId']) {
              navigate('/food-ordering/thankYou', {
                state: {
                  location: `${location?.LocationId}`,
                  pay_method: 'pay_later',
                  pay_later_status: 'payment_completed'
                },
              })
            } else {
              throw Error(`Some error occured`)
            }
          })
          .catch((err) => {
            toast.dismiss('order-processing-toast')
            setIsProcessingOrder?.(false)
            toast.error(err.message, {
              position: 'bottom-right',
            })
            navigate('/food-ordering/payment-failed',
              {
                state: {
                  location: `${location?.LocationId}`
                }
              })
          })
          .finally(() => {
            setIsLoading(false)
          })
      } else {
        const dataBody = delivPickupType == 'pickup' ? {
          ...getCompleteInvoice(),
          paymentIntentId,
          email,
          tips: formatPrice(tips),
          discounts_products: productTotal['discountedTotalPrice'] > 0 ? (productTotal['result'] - productTotal['discountedTotalPrice']) : 0,
          discount_using_promo: discountAmount,
          promo_code_applied: promoCodeDiscount,
          stripeConnectedAccount,
          type: "pickup",
          deliveryDate: convertTimeStringToDateISO(`${pickupData.day}, ${pickupData.time}`),
          delivery_phone: pickupData.phone,
          delivery_email: email,
          delivery_firstName: pickupData.firstName.split(' ')[0] ?? '',
          delivery_lastName: pickupData.firstName.split(' ')[1] ?? ''
        }
          : delivPickupType == 'delivery' ? {
            ...getCompleteInvoice(),
            paymentIntentId,
            email,
            tips: formatPrice(tips),
            discounts: (productTotal['result'] - productTotal['discountedTotalPrice']),
            stripeConnectedAccount,
            type: "delivery",
            deliveryAddress: {
              "postalCode": deliveryData.postalCode.toString(),
              "street": deliveryData.street,
              "city": deliveryData.city,
              "county": deliveryData.county,
              "long": deliveryData.long,
              "lat": deliveryData.lat,
              "country": deliveryData.country,
              "isDeliveryAddress": true,
              "isInvoiceAddress": true,
              "details": deliveryData.details
            },
            "noteForCourier": deliveryData.noteForCourier,
            "delivery_firstName": deliveryData.firstName,
            "delivery_lastName": deliveryData.lastName,
            "delivery_phone": deliveryData.phone.toString(),
            "delivery_email": deliveryData.email
          }
            : {
              ...getCompleteInvoice(),
              paymentIntentId,
              email,
              discounts_products: productTotal['discountedTotalPrice'] > 0 ? (productTotal['result'] - productTotal['discountedTotalPrice']) : 0,
              tips: formatPrice(tips),
              stripeConnectedAccount,
              discount_using_promo: discountAmount,
              promo_code_applied: promoCodeDiscount
            }
        fetch(`${process.env.REACT_APP_API_URL}/order/create`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(dataBody),
        })
          .then((res) => res.json())
          .then((data) => {
            toast.dismiss('order-processing-toast')
            if (delivPickupType == 'pickup' && (data?.orderNo || data?.paymentIntent?.newInvoiceNumber)) {
              if (data?.orderNo) window.localStorage.setItem("thankyou_code", data.orderNo)
              else if (data?.paymentIntent?.newInvoiceNumber) window.localStorage.setItem("thankyou_code", data?.paymentIntent?.newInvoiceNumber)
            }
            navigate(
              '/food-ordering/thankYou',
              {
                state: {
                  location: `${location?.LocationId}`,
                  discountAmount: discountAmount,
                  promoCodeDiscount: promoCodeDiscount
                }
              }
            )
          }).catch(err => {
            toast.dismiss('order-processing-toast')
            setIsProcessingOrder?.(false)
            toast.error(err.message, {
              position: 'bottom-right',
            })
            navigate('/food-ordering/payment-failed',
              {
                state: {
                  location: `${location?.LocationId}`
                }
              })
          }).finally(() => {
            setIsLoading(false)
          })
      }
    }
  }

  const onClick = ({ resolve }) => {
    try {
      resolve({ emailRequired: true })
    } catch (err) {
      console.error('Error in ExpressCheckout onClick:', err)
      resolve?.()
    }
  }

  const onCancel = () => {
    setIsLoading(false)
    setIsProcessingOrder?.(false)
    setProcessingStatus?.('')
  }

  const expressCheckoutOptions = useMemo(() => ({
    layout: {
      maxRows: 2,
      maxColumns: 2,
      overflow: 'auto',
    },
    buttonTheme: {
      applePay: 'white-outline',
      googlePay: 'white',
    },
    buttonType: {
      googlePay: 'plain',
      applePay: 'plain',
    },
  }), [])

  return (
    <ExpressCheckoutElement
      options={expressCheckoutOptions}
      onConfirm={onConfirm}
      onClick={onClick}
      onCancel={onCancel}
      disabled={isLoading}
    />
  )
}

// ----------------------------------------------------------------------
// Main Checkout Component – only amount calculation changed
// ----------------------------------------------------------------------
const EL_FONTS = [
  { cssSrc: 'https://fonts.googleapis.com/css?family=Poppins:400' },
]

const STRIPE_APPEARANCE = {
  theme: 'stripe',
  variables: {
    borderRadius: '24px',
    fontFamily: 'Poppins',
    colorText: '#000000',
    colorTextPlaceholder: '#B0B0B0',
    fontSizeBase: '17px',
    fontWeightNormal: '400',
    spacingUnit: '3px',
  },
}

const Checkout = ({ setIsVisible, isVisible, discountAmount, promoCodeDiscount }) => {
  const { t } = useTranslation()
  const [creditCardMethod, setCreditCardMethod] = useState('Credit Card')
  const { busket, tips, delivPickupPrice } = useContext(ProductBusketContext)
  const {
    method,
    renderCheckout,
    stripeConnectedAccount,
    location,
    uuid,
    currency,
    colorCode,
    adhocCommissionEnabled,
    adhocCommissionValue,
    stripeCommissionEnabled,
    stripeFixedCommission,
    stripePercentageCommission
  } = useContext(GlobalContext)

  const [isProcessingOrder, setIsProcessingOrder] = useState(false)
  const [processingStatus, setProcessingStatus] = useState('')

  useEffect(() => {
    if (!isVisible) {
      setIsProcessingOrder(false)
      setProcessingStatus('')
    }
  }, [isVisible])

  const getProcessingTitle = () => {
    if (processingStatus === 'creating_order') {
      return t('product.order_sent') || 'Comanda se trimite...'
    }
    return i18n.language === 'en'
      ? 'Processing payment...'
      : i18n.language === 'hu'
      ? 'Fizetés feldolgozása...'
      : 'Se procesează plata...'
  }

  const getProcessingSubtitle = () => {
    if (processingStatus === 'creating_order') {
      return i18n.language === 'en'
        ? 'We are confirming your order with the restaurant, please wait a moment...'
        : i18n.language === 'hu'
        ? 'Erősítjük megrendelését az étteremmel, kérjük, várjon...'
        : 'Confirmăm comanda cu restaurantul, vă rugăm să așteptați câteva momente...'
    }
    return i18n.language === 'en'
      ? 'Please do not close this window or refresh the page.'
      : i18n.language === 'hu'
      ? 'Kérjük, ne zárja be ezt az ablakot, és ne frissítse az oldalt.'
      : 'Vă rugăm să nu închideți această fereastră sau să reîncărcați pagina.'
  }

  let old_busket = localStorage.getItem('merged_busket');
  if (old_busket) {
    old_busket = JSON.parse(old_busket)
  }

  const productTotal = getTotalCostLocationBased(old_busket ? old_busket : busket, location.LocationId)

  // ------------------------------------------------------------------
  // NEW: Calculate net amount (what you want to receive after fees)
  // ------------------------------------------------------------------
  const netAmount = Math.max(0, (productTotal['discountedTotalPrice'] || productTotal['result']) + tips + delivPickupPrice - discountAmount)

  // Retrieve fee settings from context
  const stripeRate = stripeCommissionEnabled ? stripePercentageCommission / 100 : 0
  const stripeFixed = stripeCommissionEnabled ? stripeFixedCommission : 0
  const adhocFee = adhocCommissionEnabled ? adhocCommissionValue : 0

  // Compute the total charge (customer pays this)
  const { totalCharge, stripeFee } = computeCharges({
    netAmount,
    stripeRate,
    stripeFixed,
    adhocFee,
  })

  const retainedFee = stripeFee + adhocFee

  // totalSum is replaced by totalCharge for display
  const totalSum = totalCharge

  const [isXxxs, setIsXxxs] = useState(false)
  const { i18n } = useTranslation()

  const paymentIntentCacheRef = useRef(null)
  const paymentIntentParamsRef = useRef(null)

  useEffect(() => {
    const handleResize = () => {
      setIsXxxs(window.innerWidth < 640 && window.innerWidth > 480)
    }

    handleResize()

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const rawAmount = Number(formatPrice(totalSum)) * 100
  const roundedAmount = Math.round(rawAmount)

  const platformFeeInCents = Math.round(adhocFee * 100)
  const stripeFeeInCents = Math.round(stripeFee * 100)

  const stripeAccount =
    stripeConnectedAccount &&
    stripeConnectedAccount !== '' &&
    stripeConnectedAccount !== 'undefined' &&
    retainedFee <= 0
      ? stripeConnectedAccount
      : undefined

  const stripePromise = useMemo(() => {
    const opts = { locale: i18n.language }
    if (stripeAccount) {
      opts.stripeAccount = stripeAccount
    }
    return loadStripe(configKey, opts)
  }, [stripeAccount, i18n.language])

  const options = useMemo(() => ({
    mode: 'payment',
    amount: roundedAmount > 0 ? roundedAmount : 200,
    currency: currency ? currency.toLowerCase() : 'ron',
    fonts: EL_FONTS,
    appearance: STRIPE_APPEARANCE,
  }), [roundedAmount, currency])

  const fetchPaymentIntent = useCallback(() => {
    const params = {
      amount: roundedAmount,
      stripeConnectedAccount,
      platformFee: platformFeeInCents,
      stripeFee: stripeFeeInCents,
    }

    const paramsKey = JSON.stringify(params)
    if (paymentIntentCacheRef.current && paymentIntentParamsRef.current === paramsKey) {
      return paymentIntentCacheRef.current
    }

    paymentIntentParamsRef.current = paramsKey

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 12000)

    const promise = fetch(`${process.env.REACT_APP_API_URL}/order/create/create-payment-intent`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
      signal: controller.signal,
    })
      .then(async (res) => {
        clearTimeout(timeoutId)
        const data = await res.json()
        if (!res.ok || data?.error || !data?.clientSecret) {
          throw new Error(getPaymentIntentFailureMessage(data))
        }
        return data
      })
      .catch((err) => {
        clearTimeout(timeoutId)
        paymentIntentCacheRef.current = null
        paymentIntentParamsRef.current = null
        throw err
      })

    paymentIntentCacheRef.current = promise
    return promise
  }, [roundedAmount, stripeConnectedAccount, platformFeeInCents, stripeFeeInCents])

  // Prefetch payment intent as soon as checkout modal is open
  useEffect(() => {
    if (isVisible && roundedAmount >= 200 && method === 'online') {
      fetchPaymentIntent().catch((err) => {
        logPaymentIntentDebug('PaymentIntent prefetch error (will retry on confirm):', {
          message: err.message,
        })
      })
    }
  }, [isVisible, roundedAmount, method, fetchPaymentIntent])

  // Clear cache if modal closes
  useEffect(() => {
    if (!isVisible) {
      paymentIntentCacheRef.current = null
      paymentIntentParamsRef.current = null
    }
  }, [isVisible])

  const handleSelectCreditCardMethod = (creditCardMethod) => {
    setCreditCardMethod(creditCardMethod)
  }

  if (stripeConnectedAccount == null || renderCheckout === false) {
    if (isVisible) {
      toast.error('We are unable right now to accept online payments', {
        position: 'bottom-right',
      })
    }
    return null
  }

  if (roundedAmount < 200) return null

  return (
    <div
      className={`fixed z-50 top-0 left-0 w-full h-full bg-[#00000091] overflow-auto transition-all ${isVisible
          ? 'opacity-100 pointer-events-auto'
          : 'opacity-0 pointer-events-none'
        }`}
      onClick={() => {
        if (!isProcessingOrder) setIsVisible(false)
      }}
    >
      <div
        className="absolute sm:w-[630px] w-full top-[50%] left-[50%] bg-white shadow-lg border p-[30px] md:p-[65px] md:pr-[95px] flex flex-col rounded-[12px] relative overflow-hidden min-h-[360px]"
        onClick={(e) => e.stopPropagation()}
        style={{ transform: 'translate(-50%,-35%)' }}
      >
        {isProcessingOrder && (
          <div className="absolute inset-0 bg-white z-50 rounded-[12px] flex flex-col items-center justify-center p-6 sm:p-10 text-center">
            <div className="mb-4">
              <ReactLoading
                type="spinningBubbles"
                color={colorCode || '#000000'}
                height={64}
                width={64}
              />
            </div>
            <h3 className="text-[19px] sm:text-[21px] font-semibold text-gray-900">
              {getProcessingTitle()}
            </h3>
            <p className="mt-2 text-[14px] sm:text-[15px] text-gray-600 max-w-[360px] leading-relaxed">
              {getProcessingSubtitle()}
            </p>
          </div>
        )}

        <div className="flex justify-between items-center mb-2">
          <img
            alt="Stripe Logo"
            src="/assets/images/help-step-6.svg"
            className="w-[100px] h-[48px]"
          />
          {!isProcessingOrder && (
            <button
              className="absolute right-[10px] bg-[#FFF] transition duration-300 ease-in-out hover:bg-gray-100 hover:shadow-lg rounded-[50%] p-[10px]"
              onClick={() => setIsVisible(false)}
            >
              <RxCross2 style={{ color: '#000000' }} />
            </button>
          )}
        </div>

        <div className="flex justify-center items-center flex-col">
          <p className="font-semibold text-[18px]">
            {t('product.total_payment')}
          </p>
          <p className="font-semibold text-xl">
            {formatPrice(totalSum)} {options.currency.toUpperCase()}
            <br />
          </p>
        </div>

        {method === 'online' ? (
          <Elements stripe={stripePromise} options={options}>
            {creditCardMethod === 'Credit Card' ? (
              <>
                <ExpressCheckoutPage
                  stripeConnectedAccount={stripeConnectedAccount}
                  discountAmount={discountAmount}
                  promoCodeDiscount={promoCodeDiscount}
                  totalCharge={totalCharge}
                  adhocFee={adhocFee}
                  stripeFee={stripeFee}
                  setIsProcessingOrder={setIsProcessingOrder}
                  setProcessingStatus={setProcessingStatus}
                  fetchPaymentIntent={fetchPaymentIntent}
                />
                <CheckoutForm
                  stripeConnectedAccount={stripeConnectedAccount}
                  discountAmount={discountAmount}
                  promoCodeDiscount={promoCodeDiscount}
                  totalCharge={totalCharge}
                  adhocFee={adhocFee}
                  stripeFee={stripeFee}
                  setIsProcessingOrder={setIsProcessingOrder}
                  setProcessingStatus={setProcessingStatus}
                  fetchPaymentIntent={fetchPaymentIntent}
                />
              </>
            ) : (
              <></>
            )}
          </Elements>
        ) : null}
      </div>
    </div>
  )
}

export default Checkout
